import { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  PhoneCall, KeyRound, Save, RefreshCw, Check, AlertTriangle, Lock,
  Loader2, X, Building2, PlayCircle, PauseCircle, PhoneIncoming, PhoneOutgoing,
  Activity, CheckCircle2, XCircle, Clock, StickyNote,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  EIGHTX8_API_KEY: 'API Key',
  EIGHTX8_USERNAME: '8x8 Username',
  EIGHTX8_PASSWORD: '8x8 Password',
  EIGHTX8_PBX_ID: 'PBX ID',
  EIGHTX8_WORK_ANALYTICS_API_KEY: 'Work Analytics API Key',
  EIGHTX8_WORK_ANALYTICS_API_SECRET: 'Work Analytics API Secret',
};
const FIELD_HINTS = {
  EIGHTX8_API_KEY: 'Legacy/unused by current CDR polling — safe to leave blank',
  EIGHTX8_USERNAME: 'Required — a user with Analytics for 8x8 Work access (must have opened it in a browser once)',
  EIGHTX8_PASSWORD: 'Required — that user\u2019s 8x8 password',
  EIGHTX8_PBX_ID: 'Required — exact PBX name from Admin Console → Company → PBX',
  EIGHTX8_WORK_ANALYTICS_API_KEY: 'Required — Admin Console → API Keys → Create App → "Analytics for 8x8 Work"',
  EIGHTX8_WORK_ANALYTICS_API_SECRET: 'Not used by current CDR polling (Work Analytics auth doesn\u2019t need a Secret) — safe to leave blank',
};
// Only these 4 are actually read by the 30s CDR poller — EIGHTX8_API_KEY and
// EIGHTX8_WORK_ANALYTICS_API_SECRET exist in the schema but aren't required
// for polling to work (see services/eight_by_eight_analytics.py).
const REQUIRED_FOR_POLLING = ['EIGHTX8_PBX_ID', 'EIGHTX8_WORK_ANALYTICS_API_KEY', 'EIGHTX8_USERNAME', 'EIGHTX8_PASSWORD'];

/**
 * Settings → Integrations → 8x8. Mirrors the Zoho credentials write-only
 * pattern (encrypted at rest, never sent back to the browser), plus the
 * branch/extension assignment table and a Demo button so the whole call
 * flow can be shown live before real 8x8 credentials arrive.
 */
export const EightXEightSettings = () => {
  const [statuses, setStatuses] = useState({});
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [showStepUp, setShowStepUp] = useState(false);
  const [stepUpPassword, setStepUpPassword] = useState('');
  const [stepUpLoading, setStepUpLoading] = useState(false);
  const [stepUpToken, setStepUpToken] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [showDemo, setShowDemo] = useState(false);
  const [pollLog, setPollLog] = useState([]);
  const [pollLogLoading, setPollLogLoading] = useState(true);
  const [pollingNow, setPollingNow] = useState(false);
  const [pollingPaused, setPollingPaused] = useState(false);
  const [pausedMeta, setPausedMeta] = useState({});
  const [togglingPause, setTogglingPause] = useState(false);

  // Quick Call Note matching window (header "+Call" button feature).
  const [quickNoteWindow, setQuickNoteWindow] = useState({ pre_window_minutes: 1, post_window_minutes: 2 });
  const [quickNoteWindowLoading, setQuickNoteWindowLoading] = useState(true);
  const [quickNoteWindowSaving, setQuickNoteWindowSaving] = useState(false);

  const fetchQuickNoteWindow = async () => {
    setQuickNoteWindowLoading(true);
    try {
      const res = await api.get(`${API}/8x8/quick-notes/settings`);
      setQuickNoteWindow({
        pre_window_minutes: res.data?.pre_window_minutes ?? 1,
        post_window_minutes: res.data?.post_window_minutes ?? 2,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load quick note matching settings'); }
    finally { setQuickNoteWindowLoading(false); }
  };
  const saveQuickNoteWindow = async () => {
    setQuickNoteWindowSaving(true);
    try {
      await api.put(`${API}/8x8/quick-notes/settings`, {
        pre_window_minutes: Number(quickNoteWindow.pre_window_minutes) || 0,
        post_window_minutes: Number(quickNoteWindow.post_window_minutes) || 0,
      });
      toast.success('Matching window saved — applies from the next poll onward');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save matching window'); }
    finally { setQuickNoteWindowSaving(false); }
  };

  const fetchPollingStatus = async () => {
    try {
      const res = await api.get(`${API}/8x8/polling-status`);
      setPollingPaused(!!res.data.paused);
      setPausedMeta({ paused_at: res.data.paused_at, paused_by_email: res.data.paused_by_email });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load polling status'); }
  };
  const togglePolling = async () => {
    setTogglingPause(true);
    const nextPaused = !pollingPaused;
    try {
      const res = await api.post(`${API}/8x8/polling-status`, { paused: nextPaused });
      setPollingPaused(!!res.data.paused);
      if (nextPaused) toast.success('30s polling paused — 8x8 will not be contacted again until you unpause');
      else toast.success('Polling resumed — ran a poll now, then back on the normal 30s schedule');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update polling status'); }
    finally { setTogglingPause(false); await fetchPollingStatus(); await fetchPollLog(); }
  };

  const fetchPollLog = async () => {
    setPollLogLoading(true);
    try {
      const res = await api.get(`${API}/8x8/poll-log?limit=20`);
      setPollLog(res.data.entries || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load poll history'); }
    finally { setPollLogLoading(false); }
  };
  const pollNow = async () => {
    if (pollingPaused) { toast.warning('Polling is paused — unpause first to run a poll'); return; }
    setPollingNow(true);
    try {
      const res = await api.post(`${API}/8x8/poll-now`);
      if (!res.data.configured) toast.warning('Not configured — see Diagnostics below for what\u2019s missing');
      else if (res.data.error) toast.error(`Poll failed: ${res.data.error}`);
      else toast.success(`Polled 8x8 — ${res.data.fetched} call(s) fetched, ${res.data.matched || 0} matched to CRM`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Poll failed'); }
    finally { setPollingNow(false); await fetchPollLog(); }
  };

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/admin/integrations/8x8`);
      setStatuses(res.data.statuses || {});
      setMeta({ updated_at: res.data.updated_at, updated_by_email: res.data.updated_by_email });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load 8x8 status'); }
    finally { setLoading(false); }
  };
  const fetchBranches = async () => {
    try {
      const res = await api.get(`${API}/8x8/branch-users`);
      setBranches(res.data.branches || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load branches'); }
  };
  useEffect(() => { fetchStatus(); fetchBranches(); fetchPollLog(); fetchPollingStatus(); fetchQuickNoteWindow(); }, []);

  const openStepUp = () => setShowStepUp(true);
  const submitStepUp = async () => {
    if (!stepUpPassword.trim()) return toast.error('Password is required');
    setStepUpLoading(true);
    try {
      const res = await api.post(`${API}/admin/integrations/8x8/verify-step-up`, { password: stepUpPassword, totp_code: '' });
      setStepUpToken(res.data.step_up_token);
      setShowStepUp(false);
      setEditing(true);
      setEditValues({});
      toast.success('Verified — edit mode unlocked for 5 minutes');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Verification failed — password incorrect'); }
    finally { setStepUpLoading(false); setStepUpPassword(''); }
  };
  const cancelEdit = () => { setEditing(false); setEditValues({}); setStepUpToken(null); };
  const onSave = async () => {
    if (!stepUpToken) { toast.error('Re-verify to save'); return openStepUp(); }
    if (!Object.values(editValues).some(v => v?.trim())) return toast.error('Enter at least one field to update');
    setSaving(true);
    try {
      const res = await api.put(`${API}/admin/integrations/8x8`, { step_up_token: stepUpToken, credentials: editValues });
      toast.success(`Saved — ${res.data.fields_updated.length} field(s) updated`);
      setStepUpToken(null); setEditing(false); setEditValues({});
      await fetchStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); setStepUpToken(null); }
    finally { setSaving(false); }
  };

  const saveBranchNumber = async (branchId, value) => {
    try {
      await api.put(`${API}/8x8/branches/${branchId}/contact-number`, { contact_number: value });
      toast.success('Branch number saved');
      fetchBranches();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };
  const saveExtension = async (userId, value) => {
    try {
      await api.put(`${API}/8x8/users/${userId}/extension`, { extension: value });
      toast.success('Extension saved');
      fetchBranches();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };

  const allSet = REQUIRED_FOR_POLLING.every(f => statuses[f]?.is_set);

  return (
    <div className="space-y-5" data-testid="eightx8-settings">
      <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
        <PhoneCall className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-bold text-amber-900 mb-0.5">8x8 Telephony — {allSet ? 'Ready' : 'Awaiting credentials'}</div>
          <div className="text-amber-800">
            Once the 8x8 technician provides the API Key, username/password and PBX ID, paste them below and the
            branch/extension routing you set up here starts working immediately — no redeploy needed.
            Until then, use <strong>Demo Call</strong> below to show the flow end-to-end.
          </div>
        </div>
      </div>

      {/* Credentials */}
      <div className="rounded-xl border bg-white p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">8x8 Credentials</h3>
            {meta.updated_at && (
              <p className="text-xs text-gray-500 mt-0.5">
                Last updated {new Date(meta.updated_at).toLocaleString('en-GB')}{meta.updated_by_email ? ` by ${meta.updated_by_email}` : ''}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchStatus} disabled={loading} data-testid="8x8-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {!editing && (
              <Button size="sm" onClick={openStepUp} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="8x8-edit-btn">
                <KeyRound className="w-3.5 h-3.5 mr-1" /> Edit Credentials
              </Button>
            )}
            {editing && (
              <>
                <Button variant="outline" size="sm" onClick={cancelEdit}><X className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
                <Button size="sm" onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="8x8-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
                </Button>
              </>
            )}
          </div>
        </div>

        {!editing ? (
          <div className="divide-y divide-gray-100">
            {Object.entries(FIELD_LABELS).map(([field, label]) => {
              const s = statuses[field] || {};
              return (
                <div key={field} className="flex items-center justify-between py-2.5">
                  <span className="text-sm font-semibold text-gray-800">{label}</span>
                  {s.is_set
                    ? <span className="flex items-center gap-1 text-emerald-600 text-xs font-semibold"><Check className="w-3.5 h-3.5" /> Set</span>
                    : <span className="flex items-center gap-1 text-red-500 text-xs font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> Not set</span>}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
              <Lock className="inline w-3 h-3 mr-1" /> Hidden as you type. Leave blank to keep the existing value.
            </p>
            {Object.entries(FIELD_LABELS).map(([field, label]) => {
              const s = statuses[field] || {};
              return (
                <div key={field}>
                  <Label className="text-xs font-semibold text-gray-700">{label} {s.is_set ? <span className="text-emerald-600 font-normal">(currently set)</span> : <span className="text-red-500 font-normal">(not set)</span>}</Label>
                  <p className="text-[11px] text-gray-400 mb-1">{FIELD_HINTS[field]}</p>
                  <Input
                    type="password" autoComplete="new-password"
                    value={editValues[field] || ''}
                    onChange={(e) => setEditValues(p => ({ ...p, [field]: e.target.value }))}
                    placeholder={s.is_set ? 'Leave blank to keep existing value' : `Paste new ${label}`}
                    className="font-mono text-sm"
                    data-testid={`8x8-edit-${field}`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Call Note Matching — header "+Call" button feature */}
      <div className="rounded-xl border bg-white p-4" data-testid="eightx8-quick-note-settings-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><StickyNote className="w-4.5 h-4.5" /> Quick Call Note Matching</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Controls how a note taken via the header "+Call" button gets linked to the real 8x8 call it belongs to.
              A note is matched to a call if it was started within the window below, relative to that call's own start and end time.
              Kept tight by default — this is meant to catch a note taken <strong>while the rep is on the phone</strong>, not one written well before or after.
            </p>
          </div>
        </div>
        {quickNoteWindowLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs font-semibold text-gray-700">Minutes before call starts</Label>
              <p className="text-[11px] text-gray-400 mb-1">Covers clock/indexing skew — a note started just before 8x8's own call-start timestamp.</p>
              <Input
                type="number" min={0} max={60} step={0.5}
                value={quickNoteWindow.pre_window_minutes}
                onChange={(e) => setQuickNoteWindow(p => ({ ...p, pre_window_minutes: e.target.value }))}
                className="w-28"
                data-testid="8x8-quick-note-pre-window-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">Minutes after call ends</Label>
              <p className="text-[11px] text-gray-400 mb-1">How long after hanging up a note can still finish being typed and still count as "about this call".</p>
              <Input
                type="number" min={0} max={60} step={0.5}
                value={quickNoteWindow.post_window_minutes}
                onChange={(e) => setQuickNoteWindow(p => ({ ...p, post_window_minutes: e.target.value }))}
                className="w-28"
                data-testid="8x8-quick-note-post-window-input"
              />
            </div>
            <Button
              size="sm"
              onClick={saveQuickNoteWindow}
              disabled={quickNoteWindowSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="8x8-quick-note-window-save-btn"
            >
              {quickNoteWindowSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
            </Button>
          </div>
        )}
      </div>

      {/* Diagnostics — exactly what we asked 8x8 for, and what came back / why nothing showed */}
      <div className="rounded-xl border bg-white p-4" data-testid="eightx8-diagnostics-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Activity className="w-4.5 h-4.5" /> 8x8 Diagnostics</h3>
            <p className="text-xs text-gray-500 mt-0.5">Every poll — scheduled every 30s, or manual below — logged here with exactly what was asked for and what came back.</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={togglePolling}
              disabled={togglingPause}
              className={pollingPaused ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
              data-testid="8x8-toggle-polling-btn"
            >
              {togglingPause
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : pollingPaused ? <PlayCircle className="w-3.5 h-3.5 mr-1" /> : <PauseCircle className="w-3.5 h-3.5 mr-1" />}
              {pollingPaused ? 'Unpause Polling' : 'Pause Polling'}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchPollLog} disabled={pollLogLoading} data-testid="8x8-diagnostics-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${pollLogLoading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" onClick={pollNow} disabled={pollingNow || pollingPaused} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="8x8-poll-now-btn">
              {pollingNow ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PhoneCall className="w-3.5 h-3.5 mr-1" />} Poll Now
            </Button>
          </div>
        </div>
        {pollingPaused && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="8x8-polling-paused-banner">
            <PauseCircle className="w-4 h-4 flex-shrink-0" />
            <span>
              Polling is paused — 8x8 will not be contacted until you click "Unpause Polling"
              {pausedMeta.paused_by_email ? ` (paused by ${pausedMeta.paused_by_email})` : ''}.
              Useful if repeated failed attempts risk tripping an 8x8 account lockout while credentials are being sorted out.
            </span>
          </div>
        )}
        {pollLogLoading && pollLog.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-6">Loading…</div>
        ) : pollLog.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-6" data-testid="8x8-diagnostics-empty">No poll attempts logged yet — click "Poll Now" to run one.</div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="8x8-diagnostics-list">
            {pollLog.map((entry) => (
              <div key={entry.id} className={`rounded-lg border p-2.5 text-xs ${entry.paused ? 'border-gray-200 bg-gray-50' : entry.error ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'}`} data-testid={`8x8-diagnostics-entry-${entry.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5 font-semibold text-gray-700">
                    <Clock className="w-3 h-3" /> {new Date(entry.ran_at).toLocaleString('en-GB')}
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${entry.trigger === 'manual' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {entry.trigger === 'manual' ? 'Manual' : 'Scheduled'}
                    </span>
                  </span>
                  {entry.paused
                    ? <span className="flex items-center gap-1 text-gray-500 font-semibold"><PauseCircle className="w-3.5 h-3.5" /> Paused</span>
                    : entry.error
                      ? <span className="flex items-center gap-1 text-red-600 font-semibold"><XCircle className="w-3.5 h-3.5" /> Failed</span>
                      : <span className="flex items-center gap-1 text-emerald-600 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> OK</span>}
                </div>
                {entry.paused ? (
                  <div className="text-gray-500">Skipped — polling was paused, 8x8 was not contacted for this cycle.</div>
                ) : (
                  <>
                    <div className="text-gray-500">
                      Asked 8x8 for PBX <span className="font-mono">{entry.pbx_id || '(none)'}</span> between{' '}
                      <span className="font-mono">{new Date(entry.window_start).toLocaleTimeString('en-GB')}</span> →{' '}
                      <span className="font-mono">{new Date(entry.window_end).toLocaleTimeString('en-GB')}</span>
                    </div>
                    {!entry.error && (
                      <div className="text-gray-700 mt-1">
                        <strong>{entry.fetched}</strong> call(s) fetched · <strong>{entry.matched || 0}</strong> matched to CRM · <strong>{entry.unmatched || 0}</strong> unmatched
                        {entry.errored > 0 && <span className="text-red-600"> · <strong>{entry.errored}</strong> errored (see 8x8 Analytics tab)</span>}
                        {entry.fetched === 0 && <span className="text-amber-600"> — nothing came back for this window (call may not have appeared in 8x8's own Analytics yet, or the window missed it)</span>}
                      </div>
                    )}
                    {entry.error && <div className="text-red-700 mt-1 font-mono break-all">{entry.error}</div>}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Branch + Extension assignment */}
      <div className="rounded-xl border bg-white p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Building2 className="w-4.5 h-4.5" /> Branch Numbers & Extensions</h3>
          <Button size="sm" onClick={() => setShowDemo(true)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="8x8-demo-btn">
            <PlayCircle className="w-3.5 h-3.5 mr-1" /> Demo Call
          </Button>
        </div>
        <div className="space-y-4">
          {branches.map(branch => (
            <div key={branch.id} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm font-bold text-gray-800">{branch.display_name || branch.name}</span>
                {branch.id !== '__unassigned__' && (
                  <Input
                    defaultValue={branch.contact_number || ''}
                    placeholder="Branch DID e.g. 0121 xxx xxxx"
                    className="h-8 text-xs max-w-[220px]"
                    onBlur={(e) => e.target.value !== (branch.contact_number || '') && saveBranchNumber(branch.id, e.target.value)}
                    data-testid={`8x8-branch-number-${branch.id}`}
                  />
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pl-1">
                {(branch.users || []).map(u => (
                  <div key={u.id} className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-600 truncate flex-1">{u.full_name}</span>
                    <Input
                      defaultValue={u.extension || ''}
                      placeholder="Ext."
                      className="h-7 text-xs w-20"
                      onBlur={(e) => e.target.value !== (u.extension || '') && saveExtension(u.id, e.target.value)}
                      data-testid={`8x8-user-extension-${u.id}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Step-up dialog */}
      <Dialog open={showStepUp} onOpenChange={(o) => !o && setShowStepUp(false)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-blue-600" /> Security step-up required</DialogTitle>
            <DialogDescription>Re-enter your login password to unlock 8x8 credential editing for 5 minutes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold">Password</Label>
              <Input type="password" autoFocus value={stepUpPassword} onChange={(e) => setStepUpPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitStepUp()} data-testid="8x8-stepup-password" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowStepUp(false)}>Cancel</Button>
            <Button onClick={submitStepUp} disabled={stepUpLoading || !stepUpPassword.trim()} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="8x8-stepup-submit">
              {stepUpLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Verifying…</> : 'Verify'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DemoCallModal open={showDemo} onClose={() => setShowDemo(false)} branches={branches} />
    </div>
  );
};

function DemoCallModal({ open, onClose, branches }) {
  const allUsers = branches.flatMap(b => b.users || []);
  const [userId, setUserId] = useState('');
  const [phone, setPhone] = useState('');
  const [direction, setDirection] = useState('inbound');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const selectedUser = allUsers.find(u => u.id === userId);

  const runDemo = async () => {
    if (!userId || !phone.trim()) return toast.error('Pick a user and enter a number');
    setRunning(true);
    setResult(null);
    try {
      const res = await api.post(`${API}/8x8/demo-call`, { user_id: userId, phone_number: phone.trim(), direction });
      setResult(res.data);
      if (res.data.matched) toast.success('Matched in CRM — appears in the Calls notification bar now');
      else toast.info('No CRM match found for that number — rep can pick manually');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Demo failed'); }
    finally { setRunning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="8x8-demo-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PlayCircle className="w-5 h-5 text-purple-600" /> Demo an 8x8 Call</DialogTitle>
          <DialogDescription>Simulates what happens when a real call comes through 8x8 — pick a rep, type a number.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs font-semibold">User</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="8x8-demo-user-select"><SelectValue placeholder="Select a user…" /></SelectTrigger>
              <SelectContent className="bg-white">
                {allUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}{u.extension ? ` — ext. ${u.extension}` : ' (no extension set)'}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedUser && !selectedUser.extension && (
              <p className="text-[11px] text-red-500 mt-1">Assign this user an extension above first.</p>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setDirection('inbound')} className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg border py-1.5 text-xs font-semibold ${direction === 'inbound' ? 'bg-purple-50 border-purple-300 text-purple-700' : 'border-gray-200 text-gray-500'}`} data-testid="8x8-demo-direction-inbound">
              <PhoneIncoming className="w-3.5 h-3.5" /> Inbound
            </button>
            <button type="button" onClick={() => setDirection('outbound')} className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg border py-1.5 text-xs font-semibold ${direction === 'outbound' ? 'bg-purple-50 border-purple-300 text-purple-700' : 'border-gray-200 text-gray-500'}`} data-testid="8x8-demo-direction-outbound">
              <PhoneOutgoing className="w-3.5 h-3.5" /> Outbound
            </button>
          </div>
          <div>
            <Label className="text-xs font-semibold">Phone number</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 0121 618 7187" data-testid="8x8-demo-phone-input" />
          </div>
          {result && (
            <div className={`rounded-lg p-3 text-xs ${result.matched ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-gray-50 border border-gray-200 text-gray-600'}`}>
              {result.matched
                ? <>Matched: <strong>{result.call.account_name}</strong>{result.call.contact_name ? ` — ${result.call.contact_name}` : ''}</>
                : 'No CRM match — this call still appears in the notification bar for manual account selection.'}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={runDemo} disabled={running} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="8x8-demo-run-btn">
            {running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlayCircle className="w-4 h-4 mr-2" />} Simulate Call
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EightXEightSettings;
